import { sumBy } from "es-toolkit";

export interface ItemTotalCalculationData {
    price: number;
    wholePrice?: boolean;
    quantity: number;
}

export function calculateItemTotal(item: ItemTotalCalculationData) {
    let price = 0;
    if (item.wholePrice === true) {
        price = item.price;
    } else {
        price = item.price * item.quantity;
    }

    return price;
}

export function calculateBetterBasePrice(basePrice: number, tax: number): number {
    const rawPrice = (100 / (100 + tax)) * basePrice;

    const price1 = Math.ceil(rawPrice);
    const price2 = Math.round(rawPrice);

    if (price1 === price2) {
        return price1;
    }

    if (price1 % 10 === 0) return price1;
    if (price2 % 10 === 0) return price2;

    const taxMode = calculateBetterTax(basePrice, tax).taxMode;
    if (taxMode === "round") {
        return price2;
    } else {
        return price1;
    }
}

export function calculateBetterTax(
    basePrice: number,
    tax: number,
): {
    price: number;
    taxMode: "floor" | "round";
} {
    const rawPrice = (100 / (100 + tax)) * basePrice;

    const price1 = Math.ceil(rawPrice);
    const tax1 = Math.floor((tax / 100) * price1);
    const total1 = price1 + tax1;

    const price2 = Math.round(rawPrice);
    const tax2 = Math.round((tax / 100) * price2);
    const total2 = price2 + tax2;

    if (total1 !== total2) {
        const diff1 = Math.abs(basePrice - total1);
        const diff2 = Math.abs(basePrice - total2);

        if (diff1 < diff2) {
            return { price: price1, taxMode: "floor" };
        } else {
            return { price: price2, taxMode: "round" };
        }
    }

    return {
        price: price1,
        taxMode: "floor",
    };
}

export interface InvoiceItemsCalculationData {
    name: string;
    price: number;
    wholePrice?: boolean;
    discount: number;
    quantity: number;
    tax?: number;
}

export interface InvoiceDiscountsCalculationData {
    name: string;
    cost: number;
}

export function calculateInvoiceNumbers(
    items: InvoiceItemsCalculationData[],
    discounts: InvoiceDiscountsCalculationData[] = [],
) {
    let sumTotal = 0;
    let sumDiscount = 0;
    let taxAddition = 0;

    for (const item of items) {
        let price = 0;
        if (item.wholePrice === true) {
            price = item.price;
        } else {
            price = item.price * item.quantity;
        }

        sumTotal += price;
        sumDiscount += item.discount;
        price -= item.discount;

        const tax = (item.tax ?? 0) / 100;
        taxAddition += Math.floor(tax * price);

        // Adjust tax to make it round
        const itemTax = Math.floor(tax * price);
        if ((price + itemTax) % 10 !== 0) {
            if ((price + itemTax + 1) % 10 === 0) {
                taxAddition += 1;
            } else if ((price + itemTax - 1) % 10 === 0) {
                taxAddition -= 1;
            }
        }
    }

    let subTotal = sumTotal - Math.min(sumDiscount, sumTotal);
    const totalDiscount = sumBy(discounts, (discount) => discount.cost);
    const discountValue = Math.min(totalDiscount, subTotal);

    if (subTotal > 0) {
        const discountRatio = discountValue / subTotal;
        subTotal -= discountValue;
        taxAddition -= Math.ceil(discountRatio * taxAddition);
    }

    return {
        sumTotal: sumTotal,
        subTotal: subTotal,
        sumDiscount: sumDiscount,
        taxAddition: taxAddition,
        discountSub: discountValue,
        grandTotal: subTotal + taxAddition,
        items: items,
        discounts: discounts,
    } as const;
}
